import { Injectable } from '@angular/core';
import { 
  CreateTagRequest,
  DeleteTagRequest,
  ListTagsRequest,
  ResourceType,
  SituViewWebApiClient,
  Tag,
  UpdateTagRequest
} from 'situview-webapi-client';
import { StateService } from './state.service';

const refreshTimeInMilliseconds = 30000;
@Injectable({
  providedIn: 'root'
})

export class TaggingService {

  private _tags: Tag[] = [];
  private lastUpdated: number;
  companyId: string | undefined;

  public get tags(): Tag[] {
    const diff = performance.now() - this.lastUpdated;
    if (diff > refreshTimeInMilliseconds) {
      this.reloadTags();
    }
    return this._tags;
  }

  constructor(
    private situViewWebApiClient: SituViewWebApiClient,
    private stateService: StateService) {
      this.lastUpdated = performance.now();
      this.stateService.state$.subscribe(x => {
        this.companyId = x.company?.id ?? "";
        this.reloadTags();
      });
  }

  public tagById(tagId: string): Tag | undefined {
    return this.tags.find(t => t.id == tagId);
  }

  public tagByName(name: string): Tag | undefined {
    return this.tags?.find(tag => tag.name == name && !tag.isDeleted);
  }

  public getOrCreateTag(name: string, resourceType: ResourceType): Promise<Tag | undefined | void> {
    const existingIndex = this._tags.findIndex(tag => tag.name === name && !tag.isDeleted);
    if (existingIndex !== -1) {
      return new Promise((resolve) => {
        resolve(this._tags[existingIndex ?? -1]);
      });
    }

    // create a temporary tag that we will replace with the
    // returned tag so that the UI updates immediately.
    const newIndex = this._tags.push({
      name: name,
      companyId: this.companyId,
    } as Tag)

    return this.situViewWebApiClient.createTag({
      name: name,
      companyId: this.companyId,
      resourceType: resourceType
    } as CreateTagRequest).then((response) => {
      if (response.success) {
        // replace the temporary tag with the 'real' one
        const newTag = response.tag ?? new Tag;
        this._tags[newIndex - 1] = newTag;
        return newTag;
      }
      else {
        // remove the tag if saving it at the backend failed
        this._tags.splice(newIndex, 1);
      }
    })
    .catch(err => {});
  }

  public updateTag(tagId: string, backgroundColour: string, borderColour: string, name: string): Promise<void> {
    const tagIndex = this.cachedTagIndex(tagId);
    if (tagIndex === -1) {
      return new Promise((resolve) => {
        resolve();
      });
    }

    this._tags[tagIndex].backgroundColour ??= backgroundColour;
    this._tags[tagIndex].borderColour ??= borderColour;
    this._tags[tagIndex].name ??= name;

    return this.situViewWebApiClient.updateTag({
      tagId: tagId,
      backgroundColour: backgroundColour,
      borderColour: borderColour,
      name: name
    } as UpdateTagRequest)
    .then((response) => {
      const originalBackgroundColour = this._tags[tagIndex].backgroundColour;
      const originalBorderColour = this._tags[tagIndex].borderColour;
      const originalName = this._tags[tagIndex].name;

      if (response.success) {
        this.reloadTags();
        return;
      }
      else
        this._tags[tagIndex].backgroundColour = originalBackgroundColour;
        this._tags[tagIndex].borderColour = originalBorderColour;
        this._tags[tagIndex].name = originalName;
    })
    .catch(err => {});
  }

  public deleteTag(tagId: string) {
    const tagIndex = this.cachedTagIndex(tagId);
    if (tagIndex === -1) return;

    const req = {
      tagId: tagId
    } as DeleteTagRequest;

    this._tags[tagIndex].isDeleted = true;

    this.situViewWebApiClient.deleteTag(req).then((response) => {
      if (response.success) {
        return;
      }
      else {
        this._tags[tagIndex].isDeleted = false;
      }
    })
    .catch(err => {});
  }

  public tagClass(tag: Tag): string {
    return this.tagBackgroundColourClassName(tag?.backgroundColour ?? "") + ' ' + this.tagBorderColourClassName(tag?.borderColour ?? "");
  }

  public tagBackgroundColourClassName(tagIndex: string): string {
    return `tag-${tagIndex}-background`
  }

  public tagBorderColourClassName(tagIndex: string) {
    return `tag-${tagIndex}-border`
  }

  private cachedTagIndex(tagId: string) {
    const tagIndex = this._tags.findIndex(t => t.id === tagId);
    if (tagIndex === -1) {
      return -1;
    };

    return tagIndex;
  }

  private reloadTags() {
    const req = { companyId: this.companyId } as ListTagsRequest;
    this.situViewWebApiClient.listTags(req).then((response) => {
      if (response.success) {
        // Only update the tags if they change to avoid annoying flickering effect.
        const newTags = response.tags ?? [];
        const idGreaterThan = (a: Tag, b: Tag): number => (a.id ?? "") > (b.id ?? "") ? -1 : 1;
        const isDifferent = JSON.stringify(newTags.sort(idGreaterThan)) !== JSON.stringify(this._tags.sort(idGreaterThan))
        if (isDifferent) {
          this._tags = newTags;
        }
      }
      else {
        
      }
    })
    .catch(err => {});

    this.lastUpdated = performance.now();
  }

}
